import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import FormInput from '../../../components/FormInput';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { deacService } from '../../../services/central-api/deac';
import { AlreadyExists } from '../../../services/central-api/base-service';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandDropdown';
import LocationSelector from '../../../components/LocationSelector';
import { Tabs } from '../../../components/Tabs';
import { SelectableItem, SelectableList } from '../../../components/SelectableList';
import WarningModal from '../../../components/WarningModal';
import { centralAPI } from '../../../services/central-api';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedWorkplaceId: number | undefined;
    onConfirm: () => void;
}

export default function WorkplaceModal({ selectedWorkplaceId, isModalVisible, setModalVisible, onConfirm }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [name, setName] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(0);
    const [actingBodyCommandId, setActingBodyCommandId] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetchingData, setIsFetchingData] = useState<boolean>(selectedWorkplaceId && selectedWorkplaceId > 0 ? true : false);
    const [selectedUsers, setSelectedUsers] = useState<SelectableItem[]>([]);
    const [selectableUsers, setSelectableUsers] = useState<SelectableItem[]>([]);
    const [blockedUserIds, setBlockedUserIds] = useState<number[]>([]);

    const [showWarningModal, setShowWarningModal] = useState<boolean>(false);

    async function getWorkplace() {
        try {
            setIsFetchingData(true);
            const workplace = await deacService.getWorkplaceById(Number(selectedWorkplaceId));
            setName(workplace.name ?? '');
            setAddress(workplace.address ?? '');
            setActingBodyId(workplace.actingBodyUnit.actingBody.id);
            setActingBodyUnitId(workplace.actingBodyUnit.id);
            setActingBodyCommandId(workplace.actingBodyCommand.id);
            const selectedUsers = workplace.BlockedUsers.map((user) => {
                return {
                    id: user.id,
                    name: user.name
                };
            });
            setSelectedUsers(selectedUsers);
            setBlockedUserIds(workplace.BlockedUsers.map((user) => user.id));
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            closeModal();
        } finally {
            setIsFetchingData(false);
        }
    }

    async function validateFormSubmit() {
        if (selectedWorkplaceId) {
            const selectedUserIds = selectedUsers.map((user) => Number(user.id));
            const usersIdsToBlock = selectedUserIds.filter((selectedUserId) => !blockedUserIds.includes(selectedUserId));

            if (usersIdsToBlock.length > 0) {
                setShowWarningModal(true);
            } else {
                await formSubmit();
            }
        } else {
            await formSubmit();
        }
    }

    async function formSubmit() {
        try {
            setIsLoading(true);

            const place = await centralAPI.getPlaceByAddress(address);
            let latitude = undefined;
            let longitude = undefined;
            if (place &&
                place.candidates &&
                place.candidates.length > 0
                && place.candidates[0].geometry
                && place.candidates[0].geometry.location) {
                latitude = place.candidates[0].geometry.location.lat;
                longitude = place.candidates[0].geometry.location.lng;
            }

            if (selectedWorkplaceId) {
                const selectedUserIds = selectedUsers.map((user) => Number(user.id));
                const usersIdsToBlock = selectedUserIds.filter((selectedUserId) => !blockedUserIds.includes(selectedUserId));
                const usersIdsToUnblock = blockedUserIds.filter((blockedUserId) => !selectedUserIds.includes(blockedUserId));

                await deacService.updateWorkplaces({
                    id: selectedWorkplaceId,
                    name,
                    address,
                    latitude: latitude,
                    longitude: longitude,
                    usersIdsToBlock,
                    usersIdsToUnblock
                });
            } else {
                await deacService.createWorkplace({
                    name,
                    address,
                    latitude: latitude,
                    longitude: longitude,
                    usersIdsToBlock: selectedUsers.map(item => Number(item.id)),
                    actingBodyId,
                    actingBodyUnitId,
                    actingBodyCommandId
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
            onConfirm();
        } catch (err) {
            if (err instanceof AlreadyExists) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('workplaceAlreadyExist'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    async function getSelectableUsers(actingBodyId?: number) {
        const users = await deacService.getUsers({ actingBodyId });
        setSelectableUsers(users
            .filter((user) => !selectedUsers.find((skillUser) => skillUser.id == user.id))
            .map((user) => {
                return {
                    id: user.id,
                    name: user.name
                };
            })
        );
    }

    useEffect(() => {
        if (!authenticatedUser) {
            return;
        }

        if (actingBodyId !== 0) {
            getSelectableUsers(actingBodyId);
        }

    }, [actingBodyId, authenticatedUser]);

    useEffect(() => {
        if (isModalVisible) {
            if (selectedWorkplaceId) {
                getWorkplace();
            } else {
                if (!authenticatedUser?.permissions.deac_manage_all_acting_bodies_workplaces &&
                    authenticatedUser?.permissions.deac_manage_acting_body_units_workplaces
                ) {
                    setActingBodyId(authenticatedUser?.actingBodyUnit.actingBodyId ?? 0);
                } else if (!authenticatedUser?.permissions.deac_manage_acting_body_units_workplaces &&
                    !authenticatedUser?.permissions.deac_manage_all_acting_bodies_workplaces
                ) {
                    setActingBodyId(authenticatedUser?.actingBodyUnit.actingBodyId ?? 0);
                    setActingBodyUnitId(authenticatedUser?.actingBodyUnitId ?? 0);
                }
            }
        }
    }, [selectedWorkplaceId, isModalVisible]);

    function isFormValid(): boolean {
        if (name == '') {
            return false;
        }

        if (address.trim() === '') {
            return false;
        }

        if (actingBodyCommandId == 0) {
            return false;
        }

        if (authenticatedUser?.permissions.deac_manage_all_acting_bodies_workplaces &&
            (actingBodyId == 0 || actingBodyUnitId == 0)
        ) {
            return false;
        }

        if (authenticatedUser?.permissions.deac_manage_acting_body_units_workplaces && actingBodyUnitId == 0) {
            return false;
        }

        return true;
    }

    const closeModal = () => {
        setModalVisible(false);
        setName('');
        setAddress('');
        setSelectableUsers([]);
        setSelectedUsers([]);
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setActingBodyCommandId(0);
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    {isFetchingData ?
                        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                            <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        </View>
                        : <>
                            <Tabs tabs={[{
                                key: 'details',
                                label: translate('details')
                            }, {
                                key: 'blockedUsers',
                                label: translate('blockedUsers')
                            }]}>
                                {({ selectedTab }) => {
                                    if (selectedTab == 'details') {
                                        return (<ScrollView>
                                            <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                                <MyAppText style={styles.header}>{translate('workplaceRegistration')}</MyAppText>
                                                <MyAppText style={styles.subheader}>{translate('fillFieldsBelow')}</MyAppText>
                                            </View>
                                            <View style={{ flexDirection: 'column', rowGap: 16 }}>
                                                <FormInput
                                                    labelStyle={styles.inputLabelStyle}
                                                    onChangeText={(value) => setName(value)}
                                                    value={name}
                                                    label={translate('title')}
                                                    required
                                                />

                                                <ActingBodiesDropdown
                                                    value={actingBodyId}
                                                    setValue={setActingBodyId}
                                                    includeAllOption={false}
                                                    labelStyle={styles.inputLabelStyle}
                                                    disabled={selectedWorkplaceId != undefined || !authenticatedUser?.permissions.deac_manage_all_acting_bodies_workplaces}
                                                    required
                                                    zIndex={3}
                                                />

                                                <ActingBodiesUnitsDropdown
                                                    value={actingBodyUnitId}
                                                    setValue={setActingBodyUnitId}
                                                    actingBodyId={actingBodyId}
                                                    labelStyle={styles.inputLabelStyle}
                                                    disabled={selectedWorkplaceId != undefined ||
                                                        !(authenticatedUser?.permissions.deac_manage_all_acting_bodies_workplaces ||
                                                            authenticatedUser?.permissions.deac_manage_acting_body_units_workplaces)
                                                    }
                                                    required
                                                    zIndex={2}
                                                />

                                                <ActingBodiesCommandsDropdown
                                                    value={actingBodyCommandId}
                                                    setValue={setActingBodyCommandId}
                                                    actingBodyId={actingBodyId}
                                                    labelStyle={styles.inputLabelStyle}
                                                    disabled={selectedWorkplaceId != undefined}
                                                    required
                                                    zIndex={1}
                                                />

                                                <LocationSelector
                                                    address={address}
                                                    onAddressChange={setAddress}
                                                    label={translate('location')}
                                                    labelFontSize={14}
                                                    required
                                                />
                                            </View>
                                        </ScrollView>);
                                    }
                                    if (selectedTab == 'blockedUsers') {
                                        return <View style={{ flex: 1 }}>
                                            <SelectableList
                                                selectableLabel={translate('users')}
                                                selectableContent={selectableUsers}
                                                selectedLabel={translate('blockedUsers')}
                                                selectedContent={selectedUsers}
                                            />
                                        </View>;
                                    }
                                }}
                            </Tabs>
                            <FormActions
                                viewStyle={{ justifyContent: 'space-around' }}
                                buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                                buttonContentContainerStyle={{ flex: 1 }}
                                onSubmit={validateFormSubmit}
                                onClose={closeModal}
                                disabled={!isFormValid() || isLoading}
                                isLoading={isLoading}
                            />
                        </>}
                </View>
            </View>
            <WarningModal
                isModalVisible={showWarningModal}
                setIsModalVisible={setShowWarningModal}
                text={translate('blockingUserToWorkplace')}
                onSubmit={() => {
                    setShowWarningModal(false);
                    formSubmit();
                }}
                onClose={() => setShowWarningModal(false)}
            />
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 635,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
        height: 500,
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
}));
